import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SearchField } from '@ds/forms';
import { CommandPalette } from '@ds/navigation';
import type { Comando } from '@ds/navigation';
import { podeNaRota } from '@/router/guardas';
import { rotaPorId } from '@/router/rotas';
import type { Rota, SecaoDeNavegacao } from '@/router/rotas';
import { moduloLigado } from '@/session/sessao';

/** A rota abre para esta sessão? A mesma conta da barra lateral. */
function alcancavel(rota: Rota): boolean {
    return moduloLigado(rota.id) && rota.guards.every((g) => !g.startsWith('perm:') || podeNaRota(g.slice('perm:'.length)));
}

export interface CampoDaBuscaGlobalProps {
    onAbrir: () => void;
}

/**
 * O campo da topbar. É só a porta da paleta: a Topbar o tira no aperto, e por
 * isso o atalho e a paleta moram fora dele, em `PaletaGlobal` — senão ⌘K
 * deixaria de funcionar justamente na tela estreita.
 */
export function CampoDaBuscaGlobal({ onAbrir }: CampoDaBuscaGlobalProps) {
    return (
        <SearchField
            value="" onChange={onAbrir} placeholder="Buscar ou comandar" shortcut="⌘K"
            ariaLabel="Buscar ou comandar" width="clamp(120px, 22vw, 260px)"
        />
    );
}

export interface PaletaGlobalProps {
    nav: SecaoDeNavegacao[];
    aberta: boolean;
    onMudar: (aberta: boolean) => void;
}

/**
 * ⌘K — a busca e os comandos da topbar, porte da paleta de `App.jsx`.
 *
 * Só entra comando que funciona: os módulos e as seções que a pessoa pode abrir,
 * e "Novo agendamento", que a Agenda abre pelo endereço. Os demais comandos do
 * protótipo (novo lead, novo orçamento, perguntar à VecchIA) ainda não têm um
 * endereço que os abra — oferecê-los seria atalho para lugar nenhum.
 */
export function PaletaGlobal({ nav, aberta, onMudar }: PaletaGlobalProps) {
    const navigate = useNavigate();

    useEffect(() => {
        const aoTeclar = (e: KeyboardEvent) => {
            /* Quem já tratou a tecla (o editor de texto da ficha, por exemplo)
               fica com ela. */
            if (e.defaultPrevented) return;
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                onMudar(!aberta);
            }
        };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [aberta, onMudar]);

    const comandos: Comando[] = [];
    for (const secao of nav) {
        for (const id of secao.items) {
            const rota = rotaPorId(id);
            if (!rota || !alcancavel(rota)) continue;
            comandos.push({ id: `ir:${rota.path}`, label: rota.label, icon: rota.icon, group: secao.section });
            for (const filho of rota.children) {
                comandos.push({ id: `ir:${rota.path}/${filho.path}`, label: `${rota.label} › ${filho.label}`, icon: rota.icon, group: secao.section });
            }
        }
    }
    const agenda = rotaPorId('agenda');
    if (agenda && nav.some((s) => s.items.includes('agenda')) && alcancavel(agenda)) {
        comandos.unshift({ id: 'novo-agendamento', label: 'Novo agendamento', icon: 'calendar-plus', group: 'Ações' });
    }

    const executar = (id: string) => {
        if (id === 'novo-agendamento') void navigate('/app/agenda?modal=novo');
        else if (id.startsWith('ir:')) void navigate(id.slice('ir:'.length));
    };

    return <CommandPalette open={aberta} commands={comandos} onClose={() => onMudar(false)} onRun={executar} />;
}
